import type { DialogBarAction} from '../Types';
import type { DialogTitleProps as Props } from '@mui/material/DialogTitle';
import type { IconButtonProps } from '@mui/material/IconButton';

import Close from '@mui/icons-material/Close';
import Base from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DialogBarActions } from '../base/DialogBarActions';
import { DialogContext } from '../context/DialogContext';

import { safeArea } from '@egr/xbox/egr-gui-elements/Helper/StyleHelper';
import { FlexSpacer } from '@egr/xbox/egr-gui-elements/single-components/FlexSpacer';
import { BackIcon } from '@egr/xbox/egr-gui-icons/BackIcon';
import { makeStyles } from '@egr/xbox/utils/tss-react';

interface DialogTitleProps extends Props {
    back?: () => void;
    shouldClose?: boolean;
    'data-testid'?: string;
    actions?: Array<DialogBarAction>;
}

const useDialogTitleStyles = makeStyles({ name: 'DialogTitle' })({
    root: {
        position: 'relative',
        display: 'flex',
    },
    fullscreen: safeArea('marginTop', 'top')
});

export const DialogTitle: React.FC<DialogTitleProps> = (props) => {
    const { classes, cx } = useDialogTitleStyles();
    const { fullscreen } = React.useContext(DialogContext);

    const { shouldClose, back, children, 'data-testid': testId, actions, ...titleProps } = props;

    return (
        <Base
            {...titleProps}
            className={cx(
                classes.root,
                fullscreen ? classes.fullscreen : undefined
            )}
        >
            <DialogTitleClose
                onClick={back}
                arrow={!shouldClose}
                data-testid={testId}
            />
            {props.children}
            <FlexSpacer/>
            <DialogBarActions actions={actions}/>
        </Base>
    );
};

const useDialogTitleCloseStyles = makeStyles({ name: 'DialogTitleClose' })((theme) => ({
    placeholder: {
        width: theme.spacing(4),
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(5),
        }
    },
    button: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        left: 2,
        [theme.breakpoints.up('sm')]: {
            left: 10,
        }
    }
}));

interface DialogTitleCloseProps {
    onClick?: IconButtonProps['onClick'];
    arrow: boolean;
    'data-testid'?: string;
}

const DialogTitleClose: React.FC<DialogTitleCloseProps> = ({ arrow, ...otherProps }) => {
    const { classes } = useDialogTitleCloseStyles();
    const { t } = useTranslation();

    if (otherProps.onClick == null) {
        return null;
    }

    return (
        <React.Fragment>
            <div className={classes.placeholder}/>
            <IconButton
                {...otherProps}
                className={classes.button}
                aria-label={arrow ? t('Back') : t('Close')}
            >
                {arrow ? <BackIcon/> : <Close/>}
            </IconButton>
        </React.Fragment>
    );
};